import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Home, Clock, ArrowRightLeft, Wallet, User } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Floating glass bottom navigation modeled on the mobile-trader-template.
 * Five destinations with a centered "Trade" FAB:
 *   Home (/), Market (/market), Trade FAB (/trade), Accounts (/accounts), Profile (/profile).
 *
 * History and Deposit/Withdraw remain reachable via on-page quick actions —
 * nothing is removed.
 */
const MobileFloatingNav: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation('mobile');

    const isActive = (path: string) =>
        location.pathname === path || location.pathname.startsWith(path + '/');

    const isFabActive = isActive('/market') || isActive('/trade');

    return (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 w-[90%] max-w-md z-50 safe-area-bottom">
            <div className="flex items-center justify-between px-6 py-4 trader-glass-strong rounded-3xl shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
                <NavItem
                    icon={<Home size={24} />}
                    label={t('navHome')}
                    active={isActive('/')}
                    onClick={() => navigate('/')}
                />
                <NavItem
                    icon={<Clock size={24} />}
                    label={t('navHistory')}
                    active={isActive('/history')}
                    onClick={() => navigate('/history')}
                />

                {/* Center Trade FAB — navigates to market/instruments list */}
                <div className="relative -top-8">
                    <button
                        type="button"
                        onClick={() => navigate('/market')}
                        aria-label={t('navTrade', { defaultValue: 'Trade' })}
                        className={cn(
                            'flex items-center justify-center w-16 h-16 rounded-full text-white cursor-pointer',
                            'border-4 border-white/10 shadow-[0_8px_16px_color-mix(in_oklch,var(--primary)_40%,transparent)]',
                            'transition-all hover:scale-105 active:scale-95',
                            isFabActive
                                ? 'bg-gradient-to-tr from-emerald-400 to-emerald-600'
                                : 'trader-fab-gradient'
                        )}
                    >
                        <ArrowRightLeft size={28} />
                    </button>
                </div>

                <NavItem
                    icon={<Wallet size={24} />}
                    label={t('navAccounts')}
                    active={isActive('/accounts')}
                    onClick={() => navigate('/accounts')}
                />
                <NavItem
                    icon={<User size={24} />}
                    label={t('navProfile')}
                    active={isActive('/profile')}
                    onClick={() => navigate('/profile')}
                />
            </div>
        </div>
    );
};

const NavItem: React.FC<{
    icon: React.ReactNode;
    label: string;
    active: boolean;
    onClick: () => void;
}> = ({ icon, label, active, onClick }) => {
    return (
        <button
            type="button"
            onClick={onClick}
            className={cn(
                'flex flex-col items-center gap-1 cursor-pointer transition-colors',
                active ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
            )}
        >
            {icon}
            <span className="text-[10px] font-medium">{label}</span>
        </button>
    );
};

export default MobileFloatingNav;
